import { EventEmitter, Injectable, Output } from '@angular/core';
import { NotificationInfo } from '../interfaces/notifications.interfaces';

const NOTIFY_BAR_HEIGHT: string = '45px';
const MAT_DIALOG_CLASS: string = 'cdk-overlay-container';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private previousTabGroups: HTMLElement[] | null = null;
  private activeNotifications: NotificationInfo[] = [];
  private defaultInfo: NotificationInfo[] = [];
  @Output() public onActiveNotificationsChangedCallback = new EventEmitter<NotificationInfo[]>();

  constructor() { }

  public setDefaultInfo(info: NotificationInfo[]): void {
    this.defaultInfo = info;
  }

  public raiseNotification(notification: NotificationInfo | string): void {
    const notificationInfo = notification as NotificationInfo;
    const hadNotifications: boolean = this.activeNotifications.length > 0;

    if (notificationInfo.type !== undefined) {
      const existingNotification = this.activeNotifications.find((activeNotification: NotificationInfo) => activeNotification.type === notificationInfo.type);
      if (existingNotification !== undefined) {
        // Notification is already active, update the info
        if (existingNotification.destinationPage !== notificationInfo.destinationPage || existingNotification.destinationTab !== notificationInfo.destinationTab) {
          existingNotification.destinationPage = notificationInfo.destinationPage;
          existingNotification.destinationTab = notificationInfo.destinationTab;
          this.onActiveNotificationsChangedCallback.emit(this.activeNotifications);
        }
        return;
      }

      this.activeNotifications.push(notificationInfo);
    }
    else {
      const notificationType = notification as string;
      if (this.activeNotifications.find((activeNotification: NotificationInfo) => activeNotification.type === notificationType) !== undefined) {
        // Notification is already active
        return;
      }

      const info: NotificationInfo = this.defaultInfo.find((info: NotificationInfo) => info.type === notificationType) ?? { type: notificationType };
      this.activeNotifications.push(info);
    }

    const hasNotifications: boolean = this.activeNotifications.length > 0;
    if (hasNotifications !== hadNotifications) {
      this.fixElementSizing(true);
    }

    this.onActiveNotificationsChangedCallback.emit(this.activeNotifications);
  }

  public clearNotification(notificationType: string): void {
    const hadNotifications: boolean = this.activeNotifications.length > 0;
    this.activeNotifications = this.activeNotifications.filter((activeNotification: NotificationInfo) => activeNotification.type !== notificationType);
    const hasNotifications: boolean = this.activeNotifications.length > 0;
    if (hasNotifications !== hadNotifications) {
      this.fixElementSizing(true);
    }
    this.onActiveNotificationsChangedCallback.emit(this.activeNotifications);
  }

  public getActiveNotifications(): NotificationInfo[] {
    return this.activeNotifications;
  }

  public fixElementSizing(force: boolean = false): void {
    this.checkScssMatTabGroup(force);
    this.contentContainerFix();
  }

  private checkScssMatTabGroup(force: boolean = false): void {
    function hasNoParentOfClass(element: HTMLElement, className: string): boolean {
      while ((element = element.parentElement!) !== null) {
        if (element.classList.contains(className)) {
          return false;
        }
      }
      return true;
    }

    const elements: HTMLElement[] = Array.from(document.getElementsByClassName('mat-mdc-tab-group') as HTMLCollectionOf<HTMLElement>).filter((e) => hasNoParentOfClass(e, MAT_DIALOG_CLASS));

    if (!force && !this.shouldUpdateTabGroups(elements)) {
      return;
    }

    if (this.activeNotifications.length != 0) {
      elements.forEach((element) => {
        element.style.height = `calc(100% - 140px - ${NOTIFY_BAR_HEIGHT} - env(safe-area-inset-top))`;
      });
    }
    else {
      elements.forEach((element) => {
        element.style.top = "";
        element.style.height = "calc(100% - 140px - env(safe-area-inset-top))";
      });
    }

    this.previousTabGroups = elements;
  }

  private shouldUpdateTabGroups(matTabGroups: HTMLElement[]): boolean {
    if (this.previousTabGroups === null) {
      return true;
    }

    if (this.previousTabGroups.length !== matTabGroups.length) {
      return true;
    }

    for (let i: number = 0; i < matTabGroups.length; i++) {
      if (this.previousTabGroups[i] !== matTabGroups[i]) {
        return true;
      }
    }

    return false;
  }

  private contentContainerFix(): void {
    // We need this to make animations not overlap with the notification bar, pure CSS does not work
    const container: HTMLElement = document.getElementsByClassName('content-container-content')[0] as HTMLElement;

    if (container) {
      if (this.activeNotifications.length > 0) {
        container.style.height = `calc(100% - ${NOTIFY_BAR_HEIGHT})`;
      }
      else {
        container.style.height = '100%';
      }
    }
  }
}
